
import type { RegularFilterValueConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-value-config'
import type { QueriesByCategoryTotalsData } from '@empathybroker/tsclient-stats/dist/models/queries-by-category-totals/queries-by-category-totals-data'
import type { QueriesByCategoryTotalsResponse } from '@empathybroker/tsclient-stats/dist/models/queries-by-category-totals/queries-by-category-totals.response'
import type { Component } from 'vue/types/options'
import type { MutableListItem } from '../components/mutable-list.component.vue'
import type {
  CategoriesPath,
  CategoriesTreeItem,
  Dictionary,
  HighlightsFilterSetup,
  RegularFilter,
  RegularFilterConfig,
  RegularFilterSelectedValues,
  WidgetSetup,
} from '../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import { FilterTypeEnum } from '@empathybroker/tsclient-play/dist/enums/filter-type.enum'

import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'

import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import FiltersAside from '../components/filters/filters-aside.component.vue'

import CategoriesWidget from '../components/highlights/categories-widget.component.vue'
import CategoriesAside from '../components/highlights/categories/categories-aside.component.vue'
import KeywordsWidget from '../components/highlights/keywords-widget.component.vue'
import KpisWidget from '../components/highlights/kpis-widget.component.vue'
import NowWidget from '../components/highlights/now-widget.component.vue'
import OpportunitiesWidget from '../components/highlights/opportunities/opportunities-widget.component.vue'
import WelcomeWidget from '../components/highlights/welcome-widget.component.vue'
import { UiPopover, UiTooltip } from '../components/keen-ui'
import MutableList from '../components/mutable-list.component.vue'
import { EpbButton } from '../components/shared/epb-button'
import { EpbText } from '../components/shared/epb-text'
import { useFilters } from '../composables/use-filters'

import { ColorEnum, FeatureIdEnum, HighlightsFilterEnum, HighlightsStorageKeyEnum } from '../enums'

import { InsightsMixin } from '../mixins'

import { APP_CONSTS } from '../store/app/app.consts'
import { CONFIG_CONSTS } from '../store/config/config.consts'
import { HIGHLIGHTS_CONSTS } from '../store/highlights/highlights.consts'

import { FeaturesTreeUtils } from '../utils'

export enum WidgetEnum {
  WELCOME = 'welcome',
  OPPORTUNITIES = 'opportunities',
  KPIS = 'kpis',
  NOW = 'now',
  KEYWORDS = 'keywords',
  CATEGORIES = 'categories',
}

export default defineComponent({
  name: 'Highlights',
  components: {
    CategoriesAside,
    CategoriesWidget,
    EpbButton,
    EpbText,
    FiltersAside,
    KeywordsWidget,
    KpisWidget,
    MutableList,
    NowWidget,
    OpportunitiesWidget,
    UiPopover,
    UiTooltip,
    WelcomeWidget,
  },
  mixins: [InsightsMixin],
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      currentInstance: CONFIG_CONSTS.getters.currentInstance,
    }),
    ...mapGetters(HIGHLIGHTS_CONSTS.moduleName, {
      setupInState: HIGHLIGHTS_CONSTS.getters.setup,
    }),
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    layout(): string {
      return this.hasAnalytics ? 'layout1' : 'layout2'
    },
    hasAnalytics(): boolean {
      const overallPermission: boolean = FeaturesTreeUtils.hasFeatureNodePermission(
        FeatureIdEnum.OVERALL,
        this.featuresTree[DomainEnum.SEARCH],
      )
      const queriesPermission: boolean = FeaturesTreeUtils.hasFeatureNodePermission(
        FeatureIdEnum.QUERIES,
        this.featuresTree[DomainEnum.SEARCH],
      )
      return overallPermission && queriesPermission
    },
    selectedFilter(): HighlightsFilterSetup {
      const sections: HighlightsFilterSetup[][] = Object.values(this.filtersSetup)
      return sections.reduce(
        (filterSelected: HighlightsFilterSetup, filters: HighlightsFilterSetup[]) => {
          return filterSelected || filters.find(filter => filter.selected)
        },
        null,
      )
    },
    filtersSetup(): Dictionary<HighlightsFilterSetup[]> {
      return this.setupInState[this.currentInstance.id]?.filters || this.defaultFilters
    },
    defaultFilters(): Dictionary<HighlightsFilterSetup[]> {
      return {
        [HighlightsFilterEnum.GLOBAL]: [
          {
            id: '/',
            label: this.messages.filter.default,
            selected: true,
          },
        ],
      }
    },
    availableSections(): HighlightsFilterEnum[] {
      return Object.values(HighlightsFilterEnum).filter((section: HighlightsFilterEnum) => {
        const someFilter = {
          [HighlightsFilterEnum.FILTER]: () => this.hasAnyValidFilter,
          [HighlightsFilterEnum.CATEGORY]: () => !!this.data?.length,
          default: () => true,
        }
        return (someFilter[section] || someFilter.default)()
      })
    },
    availableFilters(): Record<string, RegularFilter> {
      return Object.values(useFilters().availableFilters.value).reduce(
        (result: Record<string, RegularFilter>, filter: RegularFilter) => {
          if (filter.config.type !== FilterTypeEnum.REGULAR) {
            return result
          }
          if (filter.config.values.length > 1) {
            const config = {
              ...filter.config,
              mandatory: true,
              multiselectDisabled: true,
            } as RegularFilterConfig

            result[config.id] = {
              config,
              selectedValues: [],
            }
          }
          return result
        },
        {},
      )
    },
    hasAnyValidFilter(): boolean {
      return Object.values(this.availableFilters).some(
        (filter: RegularFilter) => filter.config.values.length > 1,
      )
    },
    globalFilterSetup(): any {
      return this.filtersSetup[HighlightsFilterEnum.GLOBAL][0]
    },
    widgetList(): WidgetEnum[] {
      const widgetList = [WidgetEnum.WELCOME, WidgetEnum.NOW, WidgetEnum.KEYWORDS]
      widgetList.push(WidgetEnum.OPPORTUNITIES)
      widgetList.push(this.hasAnalytics ? WidgetEnum.KPIS : WidgetEnum.CATEGORIES)

      return widgetList
    },
    period(): { startDate: Date; endDate: Date } {
      const now = new Date(Date.now())
      const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
      const startDate = new Date(today.getTime())
      const endDate = new Date(today.getTime())
      startDate.setUTCDate(startDate.getUTCDate() - 7)
      return { startDate, endDate }
    },
    selectedCategory(): CategoriesTreeItem {
      if (!this.selectedFilter.categoriesPath) {
        return undefined
      }
      return this.getCategoryTreeItem(this.selectedFilter.categoriesPath)
    },
  },
  setup() {
    return {
      ColorEnum,
    }
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-highlights',
      contextFilters: null, // Record<string, RegularFilter>
      section: null, // HighlightsFilterEnum
      filterAction: null, // HighlightsFilterSetup
      mutableListFilters: {} as Record<HighlightsFilterEnum, MutableListItem[]>,
      contextCategoriesPath: [] as string[],
      filtersAsideOpened: false,
      categoriesAsideOpened: false,
      closeAsideTimeout: undefined,
      maxFiltersAllowed: {
        [HighlightsFilterEnum.FILTER]: 5,
        [HighlightsFilterEnum.CATEGORY]: 5,
      },
      messages: this.$epb.messages.highlights,
      HighlightsFilterEnum,
    }
  },
  watch: {
    filtersSetup: {
      async handler(): Promise<void> {
        this.mutableListFilters = await this.getMutableListFilters()
      },
    },
  },
  async mounted(): Promise<void> {
    this.mutableListFilters = await this.getMutableListFilters()
  },
  beforeDestroy(): void {
    clearTimeout(this.closeAsideTimeout)
  },
  methods: {
    ...mapActions(HIGHLIGHTS_CONSTS.moduleName, {
      setSetup: HIGHLIGHTS_CONSTS.actions.setSetup,
    }),
    isSectionGlobal(section: HighlightsFilterEnum): boolean {
      return section === HighlightsFilterEnum.GLOBAL
    },
    hasMaxFiltersReached(section: HighlightsFilterEnum): boolean {
      return this.filtersSetup[section]?.length >= this.maxFiltersAllowed[section]
    },
    updateSelectedFilter(filterItem: MutableListItem): void {
      if (this.isFilterSelected(filterItem)) {
        return
      }
      const filterState = this.getFilterSetupCopy()
      Object.values(filterState).forEach((filters: HighlightsFilterSetup[]) => {
        filters.forEach(filter => {
          filter.selected = filter.id === filterItem.id
        })
      })
      this.onFilterSetupChange(HighlightsStorageKeyEnum.FILTERS, filterState)
    },
    getFilterSetupCopy(): Dictionary<HighlightsFilterSetup[]> {
      return JSON.parse(JSON.stringify(this.filtersSetup))
    },
    onFilterSetupChange(
      key: HighlightsStorageKeyEnum,
      updateSetup: Dictionary<HighlightsFilterSetup[]>,
    ): void {
      const state = this.setupInState
      const instanceSetup = state[this.currentInstance.id]
      const setup = {
        ...state,
        [this.currentInstance.id]: {
          ...instanceSetup,
          [HighlightsStorageKeyEnum.FILTERS]: updateSetup,
        },
      }
      this.setSetup(setup)
    },
    onAddOrUpdateFilter(section: HighlightsFilterEnum, filterToUpdate?: MutableListItem): void {
      this.section = section
      this.filterAction = filterToUpdate

      const filterToUpdateBySection = filterToUpdate
        ? this.getFilterSetupByListItemSection(filterToUpdate, section)
        : undefined

      ;({
        [HighlightsFilterEnum.FILTER]: () => {
          const filters = this.availableFilters
          Object.keys(filters).forEach((filterKey: string) => {
            filters[filterKey].selectedValues = filterToUpdateBySection?.filters[filterKey] || []
          })
          this.contextFilters = filters
          this.filtersAsideOpened = true
        },
        [HighlightsFilterEnum.CATEGORY]: () => {
          this.contextCategoriesPath = filterToUpdateBySection?.categoriesPath || []
          this.categoriesAsideOpened = true
        },
      })[section]()
    },
    onMoveFilter(
      section: HighlightsFilterEnum,
      movement: { item: MutableListItem; targetIndex: number },
    ): void {
      const filterState = this.getFilterSetupCopy()
      const updatedFilters = filterState[section]
      const filterItem = this.getFilterSetupByListItemSection(movement.item, section)
      const oldIndex = updatedFilters.findIndex(
        (filter: HighlightsFilterSetup) => filter.id === filterItem.id,
      )
      updatedFilters.splice(oldIndex, 1)
      updatedFilters.splice(movement.targetIndex, 0, filterItem)

      this.onFilterSetupChange(HighlightsStorageKeyEnum.FILTERS, filterState)
    },
    getFilterSetupByListItemSection(
      item: MutableListItem,
      section: HighlightsFilterEnum,
    ): HighlightsFilterSetup {
      return this.filtersSetup[section]?.find(
        (filterSetup: HighlightsFilterSetup) => filterSetup.id === item.id,
      )
    },
    deleteFilter(section: HighlightsFilterEnum, filterToDelete: MutableListItem): void {
      const filterState = this.getFilterSetupCopy()
      const filterItem = this.getFilterSetupByListItemSection(filterToDelete, section)
      if (filterItem.selected) {
        filterState[HighlightsFilterEnum.GLOBAL][0].selected = true
      }
      const filters = filterState[section]
      const index = filters.findIndex(
        (filter: HighlightsFilterSetup) => filter.id === filterItem.id,
      )
      filters.splice(index, 1)

      this.onFilterSetupChange(HighlightsStorageKeyEnum.FILTERS, filterState)
    },
    async getMutableListFilters(): Promise<Record<HighlightsFilterEnum, MutableListItem[]>> {
      return Object.values(HighlightsFilterEnum).reduce(
        async (
          mutableListFiltersPromise: Promise<Record<HighlightsFilterEnum, MutableListItem[]>>,
          section: HighlightsFilterEnum,
        ) => {
          const mutableListFilters = await mutableListFiltersPromise
          mutableListFilters[section] =
            section !== HighlightsFilterEnum.GLOBAL
              ? await this.getMutableListFiltersBySection(section)
              : []
          return mutableListFilters
        },
        Promise.resolve({} as Record<HighlightsFilterEnum, MutableListItem[]>),
      )
    },
    async getMutableListFiltersBySection(
      section: HighlightsFilterEnum,
    ): Promise<MutableListItem[]> {
      const sectionFilters = this.filtersSetup[section] || []
      return Promise.all(
        sectionFilters.map(async (filter: HighlightsFilterSetup) => {
          return {
            id: filter.id,
            label: filter.label,
            editable:
              section === HighlightsFilterEnum.FILTER
                ? this.isFilterAvailable(filter)
                : await this.isCategoryAvailable(filter),
          }
        }),
      )
    },
    isFilterAvailable(filter: HighlightsFilterSetup): boolean {
      return Object.entries(filter.filters).some(
        ([id, values]: [string, RegularFilterSelectedValues]) => {
          const availableFiltersResult = Object.values(this.availableFilters).map(
            (regularFilter: RegularFilter) => ({
              id: regularFilter.config.id,
              values: regularFilter.config.values.map(
                (regularFilterValue: RegularFilterValueConfig) => regularFilterValue.id,
              ),
            }),
          )
          return availableFiltersResult.some(
            (availableFilter: { id: string; values: string[] }) =>
              availableFilter.id === id &&
              values.every((value: string) => availableFilter.values.includes(value)),
          )
        },
      )
    },
    async isCategoryAvailable(filter: HighlightsFilterSetup): Promise<boolean> {
      const categoryItem = this.getCategoryTreeItem(filter.categoriesPath)
      const categoryData = await this.fetchCategoryData(categoryItem, 1)
      return !!categoryData.length
    },
    getCategoryTreeItem(categoriesPath: string[]): CategoriesTreeItem {
      const path = [...categoriesPath]
      const level = path.length
      const id = path.pop()
      const parentId = path.pop()
      return {
        level,
        id,
        parentId,
      }
    },
    async fetchCategoryData(
      category: CategoriesTreeItem,
      rows = 0,
    ): Promise<QueriesByCategoryTotalsData[]> {
      const { id: categoryId, level, parentId } = category
      const params = {
        ...this.period,
        ...(!!rows && { rows }),
        categoryId,
        level,
        ...(!!parentId && { parentId }),
      }
      const categoryTotalsResponse: QueriesByCategoryTotalsResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.QUERIES_BY_CATEGORY_TOTALS,
        params,
      })
      return categoryTotalsResponse.data
    },
    isFilterSelected(filter: MutableListItem): boolean {
      return filter.id === this.selectedFilter.id
    },
    getAddButtonTooltip(section: HighlightsFilterEnum): string {
      const message = !this.hasMaxFiltersReached(section)
        ? this.messages.filter.maxFiltersAllowed[section]
        : this.messages.filter.maxFiltersReached[section]
      return this.$epb.utils.compileMessage(message, {
        maxFilters: this.maxFiltersAllowed[section],
      })
    },
    widgetsComponent(widget: string): Component {
      const componentList = {
        [WidgetEnum.WELCOME]: WelcomeWidget,
        [WidgetEnum.OPPORTUNITIES]: OpportunitiesWidget,
        [WidgetEnum.KPIS]: KpisWidget,
        [WidgetEnum.NOW]: NowWidget,
        [WidgetEnum.KEYWORDS]: KeywordsWidget,
        [WidgetEnum.CATEGORIES]: CategoriesWidget,
      }
      return componentList[widget]
    },
    getWidgetSetup(widget: string): WidgetSetup {
      const widgets = this.setupInState[this.currentInstance.id]?.widgets
      if (!widgets) {
        return
      }
      return widgets[widget]
    },
    onWidgetSetupChange(label: string, updateSetup: WidgetSetup): void {
      const state = this.setupInState
      const instanceSetup = state[this.currentInstance.id]
      const instanceSetupWidget = instanceSetup?.[HighlightsStorageKeyEnum.WIDGETS]

      const setup = {
        ...state,
        [this.currentInstance.id]: {
          ...instanceSetup,
          [HighlightsStorageKeyEnum.WIDGETS]: {
            ...instanceSetupWidget,
            [label]: {
              ...instanceSetupWidget?.[label],
              ...updateSetup,
            },
          },
        },
      }
      this.setSetup(setup)
    },
    onFiltersAsideUpdate(filters: Record<string, RegularFilterSelectedValues>): void {
      const { breadcrumbId: id, breadcrumbLabel: label } = Object.keys(filters).reduce(
        (
          { breadcrumbId, breadcrumbLabel }: { breadcrumbId: string; breadcrumbLabel: string },
          key: string,
        ) => {
          const filtersKey = filters[key][0]
          const prettyName =
            this.context.filters[key].config.values.find(filter => filter.id === filtersKey)
              .prettyName || filters[key][0]
          return {
            breadcrumbId: breadcrumbId ? `${breadcrumbId}/${filtersKey}` : filtersKey,
            breadcrumbLabel: breadcrumbLabel ? `${breadcrumbLabel} / ${prettyName}` : prettyName,
          }
        },
        { breadcrumbId: '', breadcrumbLabel: '' },
      )
      const filterToAddOrUpdate: HighlightsFilterSetup = {
        id,
        label,
        filters,
      }
      this.addOrUpdateFilterSetup(filterToAddOrUpdate)
    },
    addOrUpdateFilterSetup(filterToAddOrUpdate: HighlightsFilterSetup): void {
      const filterState = this.getFilterSetupCopy()
      const filtersSetup = filterState[this.section] || []

      if (this.filterAction) {
        const index = filtersSetup.findIndex(
          (filter: HighlightsFilterSetup) => filter.id === this.filterAction?.id,
        )
        filtersSetup[index] = {
          ...filtersSetup[index],
          ...filterToAddOrUpdate,
        }
      } else {
        filterState[this.section] = [...filtersSetup, filterToAddOrUpdate]
      }
      this.onFilterSetupChange(HighlightsStorageKeyEnum.FILTERS, filterState)
      this.updateSelectedFilter(filterToAddOrUpdate)
    },
    onAsideClose(type: HighlightsFilterEnum): void {
      clearTimeout(this.closeAsideTimeout)
      this.closeAsideTimeout = setTimeout(() => {
        ;({
          [HighlightsFilterEnum.FILTER]: () => (this.filtersAsideOpened = false),
          [HighlightsFilterEnum.CATEGORY]: () => (this.categoriesAsideOpened = false),
        })[type]()
      }, 500)
    },
    onCategoriesAsideUpdate(categoriesPathObject: CategoriesPath[]): void {
      const categoryFilterSetup = this.getCategoryFilterSetup(categoriesPathObject)
      this.addOrUpdateFilterSetup(categoryFilterSetup)
    },
    getCategoryFilterSetup(categoriesPathObject: CategoriesPath[]): HighlightsFilterSetup {
      const categoriesPath = categoriesPathObject.map(child => child.id)
      const id = categoriesPath.join('/')
      const label = categoriesPathObject.map(child => child.name).join(' / ')

      return {
        id,
        label,
        categoriesPath,
      }
    },
    async fetchData(): Promise<QueriesByCategoryTotalsData[]> {
      const params = {
        ...this.period,
        rows: 0,
        level: 1,
      }
      const queriesByCategoryTotalsResponse: QueriesByCategoryTotalsResponse =
        await this.fetchStatsReport({
          id: StatsReportsIdsEnum.QUERIES_BY_CATEGORY_TOTALS,
          params,
        })
      return queriesByCategoryTotalsResponse.data
    },
  },
})
